import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Broadcast, Users, FileText, ChatsCircle, ArrowUpRight } from "@phosphor-icons/react";
import { PageHeader, StatusBadge, BillingBadge, formatDate } from "@/components/shared";
import { useNavigate } from "react-router-dom";

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="bg-white p-6 rounded-lg flex flex-col gap-4 transition-colors hover:bg-[#FCFCFC]" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#767676]">{label}</span>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" weight="regular" />
      </div>
    </div>
    <span className="font-heading text-4xl font-bold text-[#111111] leading-none">{value}</span>
  </div>
);

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [tplStatus, setTplStatus] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data));
    api.get("/dashboard/recent-messages").then((r) => setRecent(r.data || []));
    api.get("/dashboard/template-status-summary").then((r) => setTplStatus(r.data || {}));
  }, []);

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Ringkasan aktivitas WhatsApp Business Anda." />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <StatCard icon={Broadcast} label="Channel" value={summary?.total_channels ?? "-"} accent="bg-[#003399] text-white" testid="stat-channels" />
        <StatCard icon={Users} label="Kontak" value={summary?.total_contacts ?? "-"} accent="bg-[#0A8A0014] text-[#0A8A00]" testid="stat-contacts" />
        <StatCard icon={FileText} label="Template" value={summary?.total_templates ?? "-"} accent="bg-[#FFDA1A] text-[#111111]" testid="stat-templates" />
        <StatCard icon={ChatsCircle} label="Percakapan" value={summary?.total_conversations ?? "-"} accent="bg-[#F0F2F5] text-[#111111]" testid="stat-conversations" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-lg">
          <div className="px-6 py-4 border-b border-[#F0F0F0] flex items-center justify-between">
            <h3 className="font-heading text-lg font-bold text-[#111111]">Pesan Terbaru</h3>
            <button onClick={() => navigate("/messages")} className="text-sm text-[#003399] font-semibold flex items-center gap-1 hover:underline" data-testid="view-all-messages">
              Lihat semua <ArrowUpRight className="w-3.5 h-3.5" weight="bold" />
            </button>
          </div>
          <div className="divide-y divide-[#F5F5F5]">
            {recent.length === 0 && <p className="px-6 py-8 text-sm text-[#767676] text-center">Belum ada pesan.</p>}
            {recent.map((m) => (
              <div key={m.id} className="px-6 py-3 flex items-center gap-4" data-testid={`recent-message-${m.id}`}>
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${m.direction === "inbound" ? "bg-[#0A8A00]" : "bg-[#003399]"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#111111] truncate">{m.contact_name || m.to_number}</p>
                  <p className="text-xs text-[#767676] truncate">{m.content?.text || `[${m.message_type}]`}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <BillingBadge billing={m.billing} />
                  <div className="text-right">
                    <StatusBadge status={m.status} />
                    <p className="text-xs text-[#767676] mt-1">{formatDate(m.created_at)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg">
          <div className="px-6 py-4 border-b border-[#F0F0F0]">
            <h3 className="font-heading text-lg font-bold text-[#111111]">Status Template</h3>
          </div>
          <div className="p-6 space-y-4">
            {["APPROVED", "PENDING", "DRAFT", "REJECTED", "DISABLED"].map((s) => (
              <div key={s} className="flex items-center justify-between" data-testid={`tpl-status-${s}`}>
                <StatusBadge status={s} />
                <span className="font-heading text-lg font-bold text-[#111111]">{tplStatus[s] ?? 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
